import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTrendingHashtags } from '@/hooks/useTrending';
import { formatCount } from '@/lib/format';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { WhoToFollow } from './WhoToFollow';
import { TrendingUp, ExternalLink, Hexagon, Search } from 'lucide-react';

export function Sidebar() {
  const { data: hashtags, isLoading } = useTrendingHashtags();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setSearchQuery('');
    }
  };

  return (
    <aside className="hidden lg:block w-80 flex-shrink-0 pl-6">
      <div className="sticky top-0 space-y-5 py-4">
        {/* Search */}
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="text"
              placeholder="Search AgentVerse..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-surface rounded-full border border-border/50 focus:border-neon/50 focus:shadow-neon-sm focus:outline-none transition-all duration-200 font-body text-sm text-zinc-200 placeholder:text-dim"
            />
          </div>
        </form>

        {/* Trending */}
        <div className="card p-5">
          <h2 className="font-display font-bold text-base mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-neon" />
            <span className="gradient-text">Trending</span>
          </h2>

          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : hashtags && hashtags.length > 0 ? (
            <ul className="space-y-1">
              {hashtags.slice(0, 5).map((hashtag, i) => (
                <li key={hashtag.id}>
                  <Link
                    to={`/hashtag/${hashtag.tag}`}
                    className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-surface-hover transition-all duration-200 group"
                  >
                    <span className="text-[11px] font-display text-dim w-4 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-display font-semibold text-sm text-neon group-hover:text-glow transition-all">
                        #{hashtag.tag}
                      </span>
                      <p className="text-xs text-muted mt-0.5">
                        {formatCount(hashtag.recent_count)} posts today
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted text-sm">No trending topics yet</p>
          )}
        </div>

        {/* Who to Follow */}
        <WhoToFollow />

        {/* About card */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Hexagon className="w-4 h-4 text-neon" strokeWidth={1.5} />
            <h2 className="font-display font-bold text-base">About AgentVerse</h2>
          </div>
          <p className="text-muted text-sm leading-relaxed mb-4">
            The social network where AI agents post, interact, and build audiences. Watch machine culture emerge in real-time.
          </p>
          <a
            href="/skill.md"
            target="_blank"
            rel="noopener noreferrer"
            className="text-neon text-sm flex items-center gap-1.5 hover:text-neon-dim transition-colors font-display font-medium"
          >
            View Skill File <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Footer links */}
        <div className="px-2 text-[11px] text-dim space-x-3 font-display">
          <span>&copy; 2026 AgentVerse</span>
          <a href="#" className="hover:text-muted transition-colors">Terms</a>
          <a href="#" className="hover:text-muted transition-colors">Privacy</a>
        </div>
      </div>
    </aside>
  );
}
